// MMP1 - FH Salzburg - MultiMediaTechnology
// Tobias Franek <tfranek.mmt-b2018@fh-salzburg.ac.at>

import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import './CommentBox.scss';
import { observable } from 'mobx';
import OverflowDecorator from './Decorators/OverflowDecorator';
import { Editor, EditorState, CompositeDecorator, Modifier, ContentBlock, ContentState } from 'draft-js';
import CircleLoader from '../../Util/CircleLoader/CircleLoader';
import Textarea from 'react-textarea-autosize';

@inject('messageStore')
@observer class CommentBox extends Component<any, any> {

	maxLength: number = 140;

	@observable editorState: any;

	@observable loading: boolean = false;

	@observable textarea: string = '';

	overflow: boolean = false;

	compositeDecorator: CompositeDecorator;

	isMobileDevice: boolean = false;

	progressOptions = {
		radius: 44,
		strokeWidth: 16,
		backgroundColor: '#D9D9D9',
		progressColor: '#0094E9',
		progressFullColor: '#ef3434'
	}

	constructor(props: any) {
		super(props);
		this.overflowStrategy = this.overflowStrategy.bind(this);

		this.compositeDecorator = new CompositeDecorator([
			{
				strategy: this.overflowStrategy,
				component: OverflowDecorator,
			}
		]);

		this.editorState =  EditorState.createEmpty(this.compositeDecorator);

		this.isMobileDevice = this.checkIfMobileDevice();

		this.onChange = this.onChange.bind(this);
		this.onTextareaChange = this.onTextareaChange.bind(this);
		this.handlePastedText = this.handlePastedText.bind(this);
		this.calculateProgress = this.calculateProgress.bind(this);
		this.send = this.send.bind(this);
		this.handleReturn = this.handleReturn.bind(this);
	}

	checkIfMobileDevice() {
		const userAgent = navigator.userAgent || navigator.vendor;
		if (/windows phone/i.test(userAgent) || /android/i.test(userAgent) || /iPad|iPhone|iPod/.test(userAgent)) {
			return true;
		}
	
		return false;
	
	}

	overflowStrategy(contentBlock: ContentBlock, callback: any, contentState: ContentState): void {
		const blocks = contentState.getBlocksAsArray();
		let charCount = 0;
		let startingBlocks = [];
		for(let block in blocks) {
			if (charCount > this.maxLength) {
				break;
			}
			startingBlocks.push(blocks[block].getKey());
			charCount += (blocks[block].getText().length + 1);		
		}
		const isStartingBlock = startingBlocks.indexOf(contentBlock.getKey()) > -1 ? true : false;
		if (contentState.getPlainText().length >= this.maxLength) {
			if (isStartingBlock) {
				let offset = 0;
				let act = contentState.getBlockBefore(contentBlock.getKey());
				while (act) {
					offset += act.getText().length + 1;
					act = contentState.getBlockBefore(act.getKey());
				}
				callback(this.maxLength - offset, contentBlock.getText().length)
			} else {
				callback(0, contentBlock.getText().length)
			}
		}
	}

	handlePastedText(text: string, html: string, editorState: EditorState): any {
		const contentState = Modifier.replaceText(editorState.getCurrentContent(), editorState.getSelection(), text);
		const newEditorState = EditorState.createWithContent(contentState, this.compositeDecorator);
		this.onChange(newEditorState);
		return true;
	}

	handleReturn(e: any, editorState: EditorState): any {
		if (!e.shiftKey) {
			this.send();
			return true;
		} 
		return false;
	}

	onChange(editorState: EditorState) {
		this.editorState = editorState;
	}

	calculateProgress(maxValue: number) {
		if (this.currentCharacters() < this.maxLength) {
			const currentPercentage = this.currentCharacters() / this.maxLength;
			return maxValue - (currentPercentage * maxValue);
		} else {
			return 0;
		}
	}

	send() {
		if (this.currentCharacters() > this.maxLength) {
			this.props.messageStore.setMessage('error', 'you are over the character limit');
		} else {
			if (!this.loading) {
				this.loading = true;
				this.props.onSubmit(this.isMobileDevice ? this.textarea : this.editorState.getCurrentContent().getPlainText()).then((response: boolean) => {
					this.loading = false;
					if (response) {
						let editorState = EditorState.moveSelectionToEnd(EditorState.createEmpty(this.compositeDecorator));
						editorState = EditorState.moveFocusToEnd(editorState);

						this.onChange(editorState);
						this.textarea = '';
						this.props.messageStore.setMessage('success', 'successfully created a new comment', 2000);
					}
				});
			}
		}
	}

	currentCharacters(): number {
		if (this.isMobileDevice) {
			return this.textarea.length;
		} else {
			return this.editorState.getCurrentContent().getPlainText().length;
		}
	}

	onTextareaChange(e: any) {
		this.textarea = e.target.value;
	}

	render() {
		return (
			<div className="comment-box">
				{this.isMobileDevice ? 
					<Textarea placeholder="comment..." value={this.textarea} onChange={this.onTextareaChange} />
					:
					<Editor 
						editorState={this.editorState} 
						onChange={this.onChange}
						placeholder="comment..."
						handlePastedText={this.handlePastedText}
						handleReturn={this.handleReturn}
					/>}
				
				<div className="comment-send-button-wrapper">
					{
						this.currentCharacters() > this.maxLength ? 
							<span className="overflow-number">
								{this.maxLength - this.currentCharacters()}
							</span>
						:
							null
					}
					
					<button 
						className={
							'comment-send-button icon-paper-plane ' + 
							(this.currentCharacters() > this.maxLength || this.currentCharacters() === 0 ? 'disabled ' : '')
							+ (this.loading ? 'loading' : '')
						}
						onClick={this.send}
						>
					</button>
					{this.loading ?
						<CircleLoader color='#fff' size={25} />
						:
						null
					}
					<svg className="svg-circle-progress" viewBox="0 0 100 100" version="1.1" xmlns="http://www.w3.org/2000/svg">
						<circle 
							cx="50" 
							cy="50" 
							r={this.progressOptions.radius}
							fill="none" 
							stroke={this.progressOptions.backgroundColor} 
							strokeWidth={this.progressOptions.strokeWidth} 
						/>
						<circle 
							cx="50" 
							cy="50" 
							r={this.progressOptions.radius}
							fill="none" 
							stroke={this.currentCharacters() > this.maxLength ? this.progressOptions.progressFullColor : this.progressOptions.progressColor} 
							strokeWidth={this.progressOptions.strokeWidth}
							strokeDasharray={2 * Math.PI * this.progressOptions.radius} 
							strokeDashoffset={this.calculateProgress((2 * Math.PI * this.progressOptions.radius))}
						/>
					</svg>
				</div>
			
			</div>
		)
	}
}

export default CommentBox;

